import { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from 'components/Kizen/Loader';
import SubNav from './components/SubNav';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BUSINESS, DASHBOARD } from 'queries/query-keys';
import { v4 as uuidv4 } from 'uuid';
import {
  BreakpointContainer,
  DashboardErrorCodeText,
  DashboardWarningText,
} from './styles';
import { DEFAULT_NAME_KEY, buildDashletConfig } from './util';
import { getRoles } from './queries';
export {
  getActivityIds,
  getChartType,
  getFeExtraInfo,
  getIncludeEmployees,
  getIncludeEmployeesWithRoles,
  getIncludeRoles,
  getLeadSources,
  getMetricType,
  getObjectId,
  getObjectIds,
  getReportTypeForBackend,
  getStages,
  getCalendarTimePeriod,
  getShowingOnlyWorkingDays,
} from './util';
import useModal from 'components/Modals/useModal';
import DashletModal from './components/Dashlet/modal';
import BlockModal from 'pages/Homepages/components/PluginBlock/modal';
import { HomepageBuilder } from 'ts-components/StaticBlockBuilder/HomepageBuilder';
import { breakpoints, isMobile, useWindowSize } from 'app/spacing';
import 'react-grid-layout/css/styles.css';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import useDashboardConfig from './hooks/useDashboardConfig';
import { useHistory, useParams, useLocation, Redirect } from 'react-router-dom';
import ResettableDashlet from './components/Dashlet/Resettable';
import { useSelector } from 'react-redux';
import { useDashboardTimeZoneNotice } from './hooks/useDashboardTimeZoneNotice';
import { DashboardStateWrapper } from './context/persistentState';
import BasicModalWithConfirmation from 'components/Modals/presets/BasicModalWithConfirmation';
import { ModalContentWrapper } from 'ts-components/RecordLayout/blocks/TeamActivities';
import DashboardGrid from 'components/DashboardGrid';
import useDashboardGridChildren, {
  MODES,
} from 'components/DashboardGrid/hooks/useDashboardChildren';
import {
  DashboardGridContainerBox,
  DashboardGridPageContainer,
} from 'components/DashboardGrid/styles';
import {
  createDashboardQuery,
  fetchDashboardsQuery,
  requestAccessQuery,
} from 'components/AccessRequests/useDashboardList';
import { getSortedDashboards } from 'components/DashboardGrid/utils';
import useDashboardInteraction from 'components/DashboardGrid/hooks/useDashboardInteraction';
import {
  useStaticContentBlock,
  isStaticContentDashlet,
} from 'components/DashboardGrid/hooks/useStaticContentBlock';
import { getDashletDimensions } from 'components/DashboardGrid/dimensions';
import CreateLayoutModal from 'components/DashboardGrid/CreateLayoutModal';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import { ErrorMessage } from 'components/ErrorMessage';
import Button from 'components/Button';
import { toastVariant, useToast } from 'components/ToastProvider';
import RequestModal from 'components/AccessRequests/modal';
import { DataManagerWrapper } from './context/dataManager/context';
import {
  getBusinessClientObject,
  getDashboardsAccess,
} from 'store/authentication/selectors';
import ScheduledActivities from 'components/Charts/ScheduledActivities';
import getDashboardUrl from 'utility/getDashboardUrl';
import { deferExecution } from 'utility/defer';
import { editableDashlet } from './util';
import { isFlagEnabled } from 'debug/flags';
import {
  DashboardStyleSettingsProvider,
  DEFAULT_DASHLET_STYLE_SETTINGS,
} from 'ts-components/StyleSettings';
import { getDashletWithMergedStyles } from 'ts-components/StyleSettings/utils';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { GUARD_RAIL_CODES } from 'ts-components/Conversation';
import {
  OverflowPortalContext,
  PortalContainer,
} from 'components/OverflowPortal';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { PluginContext } from 'ts-components/Plugins/context';
import PluginBlockWizard from 'pages/Homepages/components/PluginBlock/PluginBlockWizard';
import {
  usePluginContentBlock,
  isPluginContentDashlet,
} from 'components/DashboardGrid/hooks/usePluginContentBlock';
import { useCustomCodeBlock } from 'components/DashboardGrid/hooks/useCustomCodeBlock';
import { isCustomCodeDashlet } from 'components/DashboardGrid/hooks/customCodeBlock';
import { CustomCodeBlockEditor } from 'pages/Homepages/components/CustomCodeBlock/CustomCodeBlockEditor';
import { useAiBlockSession } from 'ts-components/AiBlockPanel/useAiBlockSession';
import {
  AI_BLOCK_TYPE,
  toAiBlockSafeUpdate,
} from 'ts-components/AiBlockPanel/aiBlockSession';
import { PiChatSideLayout } from 'ts-components/PiChat/PiChatSideLayout';

const setDashboardTitle = (title, dashboard) => {
  const { name } = dashboard ?? {};
  return name ? `${name} - ${title}` : title;
};
const scheduledActivityPopupQueryParam = 'view_scheduled_activity_id';

const DashboardPage = ({ title, activityId = '' }) => {
  const { t } = useTranslation();
  const [overflowPortalContainer, setOverflowPortalContainer] = useState(null);

  const { width } = useWindowSize();
  const mobile = isMobile(width, breakpoints.md);
  const history = useHistory();
  const location = useLocation();
  const [showToast] = useToast();
  const [activityNoteDirty, setActivityNoteDirty] = useState(false);
  const [existingDashboard, setExistingDashboard] = useState({});
  const [activityIdValue, setActivityIdValue] = useState(activityId);
  const [currentDashlet, setCurrentDashlet] = useState({});
  const [overrideShowDashletMenu, setOverrideShowDashletMenu] = useState(false);
  const [autoOpenAIOverlay, setAutoOpenAIOverlay] = useState(false);
  const [pluginEditDashlet, setPluginEditDashlet] = useState(null);

  const { pluginBlocks } = useContext(PluginContext);

  const dashboardsAccess = useSelector(getDashboardsAccess);

  const clientObject = useSelector(getBusinessClientObject);

  const clientObjectId = clientObject.id;
  const { aiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.dashboard_builder
  );

  const { aiFeaturesEnabled: dashletAiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.dashlet_builder
  );

  const { isLoading: rolesLoading, data: roles } = useQuery({
    queryKey: BUSINESS.ROLES,
    queryFn: getRoles,
  });
  const {
    isLoading: dashboardsLoading,
    data,
    refetch: refetchDashboards,
  } = useQuery({
    queryKey: [...DASHBOARD.DASHBOARDS, 'list'],
    queryFn: () => {
      return fetchDashboardsQuery(null, false, undefined, 'generic_dashboard');
    },
    throwOnError: false,
  });

  const sortedDashboards = useMemo(
    () => getSortedDashboards(data ?? []),
    [data]
  );

  useDashboardTimeZoneNotice(sortedDashboards);

  const hasDashboards = useMemo(() => {
    const filtered = sortedDashboards.filter((dashboard) => !dashboard.hidden);

    return filtered.length > 0;
  }, [sortedDashboards]);

  const sendRequestMutation = useMutation({
    mutationFn: (result) => {
      return requestAccessQuery(
        result.dashboardId,
        result.adminId,
        result.accessLevel
      );
    },
    onSettled: (data, e) => {
      if (data?.status === 'pending') {
        showToast({
          message: t('Your access request has been sent.'),
          variant: toastVariant.SUCCESS,
        });
      } else {
        showToast({
          message: t('Your access request could not be sent.'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const [requestModalProps, , { show: handleShowRequestModal }] = useModal({
    handleSubmit: (dashboardId, accessLevel, adminId) =>
      sendRequestMutation.mutate({ dashboardId, accessLevel, adminId }),
    handleHide: () => {
      setExistingDashboard({});
    },
  });

  const [
    editDashletModalProps,
    ,
    { show: handleShowEditDashlet, hide: handleHideEditDashlet },
  ] = useModal({
    handleSubmit: (newDashlet, _payload, isScheduledActivitiesColumnUpdate) => {
      setCurrentDashlet({});
      return mutateDashletForDashboardOnly({
        dashboardId: currentDashboard.id,
        dashletData: { ...newDashlet, id: currentDashlet.id },
        isUpdate: true,
        layoutUpdate: isScheduledActivitiesColumnUpdate,
      });
    },
  });

  const {
    loading: configLoading,
    currentDashboard,
    dateFilter,
    setDateFilter,
    teamFilter,
    setTeamFilter,
    refetchDashboard,
    dashboardQueryKey,
    currentDashboardPermission,
    setPersistentDashboardState,
    persistentDashboardState,
    afterCreate,
    dashboardError,
    unsafeDashboardId,
    ranges,
    todayDateInBusinessTimeZone,
    setCurrentDashboardId,
  } = useDashboardConfig(
    sortedDashboards,
    undefined,
    'generic_dashboard',
    undefined,
    true
  );

  const {
    renderFocusSlot: renderAiFocusSlot,
    openCreate: openAiBlockCreate,
    openEdit: openAiBlockEdit,
    sessionDashletId: aiSessionDashletId,
    panel: aiBlockPanel,
  } = useAiBlockSession({
    targetType: 'dashboard',
    dashboardId: currentDashboard?.id,
    dashlets: currentDashboard?.dashlets,
    dashboardName: currentDashboard?.name,
  });

  useEffect(() => {
    const dashboardPathPattern = /^\/dashboard\/[^/]+$/;
    const hasDashboardId = dashboardPathPattern.test(location.pathname);
    if (activityId && hasDashboardId) {
      setActivityIdValue(activityId);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(scheduledActivityPopupQueryParam);

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [
    activityId,
    setActivityIdValue,
    location.pathname,
    location.search,
    history,
  ]);

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.dashboards?.guardrail_code;
    const guardrailMessage = runMetadata?.dashboards?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedDashboard =
      Object.keys(runMetadata?.dashboards || {}).length > 0
        ? Object.values(runMetadata?.dashboards)?.[0]
        : null;

    return {
      hasResult: !!generatedDashboard,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const redirectToGeneratedDashboard = useCallback(
    async (dashboardPath) => {
      await refetchDashboards();
      deferExecution(() => {
        history.push(dashboardPath);
      });
    },
    [history, refetchDashboards]
  );

  const onGenerationSuccess = useCallback(
    (dashboardFlowStatusData) => {
      const guardrailCode =
        dashboardFlowStatusData.run_metadata?.guardrail_code;
      const guardrailMessage =
        dashboardFlowStatusData.run_metadata?.guardrail_message;
      const navigateUrl = dashboardFlowStatusData?.next_actions?.[0]?.url;
      if (navigateUrl) {
        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Dashboard Generated Successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            redirectToGeneratedDashboard(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            extra: {
              nextActionUrl: navigateUrl,
            },
          });
        }
      }

      // Handle one-shot response with run_metadata
      const generatedDashboard =
        Object.keys(dashboardFlowStatusData.run_metadata?.dashboards || {})
          .length > 0
          ? Object.values(dashboardFlowStatusData.run_metadata?.dashboards)?.[0]
          : null;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (generatedDashboard) {
          setTimeout(() => {
            redirectToGeneratedDashboard(`/dashboard/${generatedDashboard.id}`);
          }, 1500);
        }
      } else if (generatedDashboard) {
        showToast({
          message: t('Dashboard Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          redirectToGeneratedDashboard(`/dashboard/${generatedDashboard.id}`);
        }, 1500);
      } else {
        showToast({
          message: t('Dashboard Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t, redirectToGeneratedDashboard]
  );
  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Dashboard Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t]
  );

  const isLoading = rolesLoading || dashboardsLoading || configLoading;

  useSetTitleOnLoad(setDashboardTitle(title(t), currentDashboard));

  const missingPermission = useMemo(() => {
    if (isLoading) {
      return false;
    }

    return (
      dashboardError?.response?.status === 403 && Boolean(unsafeDashboardId)
    );
  }, [isLoading, dashboardError, unsafeDashboardId]);

  const doesNotExist = useMemo(() => {
    if (isLoading) {
      return false;
    }

    // This is a bit of a hack to get around the issue when the backend
    // rejects the request with a 302 because of a malformed UUID
    if (dashboardError && !dashboardError.response) {
      return Boolean(unsafeDashboardId);
    }

    return (
      dashboardError?.response?.status === 404 && Boolean(unsafeDashboardId)
    );
  }, [isLoading, dashboardError, unsafeDashboardId]);

  const {
    breakpoint,
    setBreakpoint,
    rowHeight,
    setContainerWidth,
    editable,
    draggable,
    hasEditPermission,
    dashlets,
    resizeHandles,
    getDashletPosition,
    duplicateDashlet,
    createDashletMutation,
    updateDashletMutation,
    deleteDashlet,
    updateLayouts,
    forceShowUIElements,
  } = useDashboardInteraction({
    currentPermission: currentDashboardPermission,
    refetchDashboard,
    entityName: t('Dashlet'),
    dashboardQueryKey,
    currentDashboard,
    refetchDashboards,
    isLoading,
    canEdit: dashboardsAccess.edit,
    aiSessionDashletId,
  });

  const {
    mutateStaticContentBlock,
    openStaticContentBuilder,
    staticContentBuilderOpen,
    staticContentBuilderProps,
  } = useStaticContentBlock({
    currentDashboard,
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutatePluginBlock } = usePluginContentBlock({
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutateCustomCodeBlock, openCustomCodeEditor, customCodeEditorProps } =
    useCustomCodeBlock({
      currentDashboard,
      dashlets,
      updateDashletMutation,
      duplicateDashlet,
    });

  const mutateDashletForDashboardOnly = useCallback(
    async ({
      dashboardId,
      dashletData,
      isUpdate = false,
      layoutUpdate = false,
      isDuplicate = false,
    }) => {
      const oldDashlet = dashlets.find((d) => d.id === dashletData.id);
      const dimensions = getDashletDimensions(dashletData);
      const position = getDashletPosition(dimensions);
      const payload = {
        config: buildDashletConfig(dashletData),
        layout: {
          i: uuidv4(),
          ...dimensions,
          ...position,
        },
        name: dashletData.name || DEFAULT_NAME_KEY,
      };

      if (isUpdate && oldDashlet) {
        const updatePayload = {
          dashletId: dashletData.id,
          dashboardId,
          payload: {
            config: payload.config,
            name: payload.name || DEFAULT_NAME_KEY,
            layout: {
              i: oldDashlet.layout.i,
              h: oldDashlet.layout.h,
              w: oldDashlet.layout.w,
              x: oldDashlet.layout.x,
              y: oldDashlet.layout.y,
            },
          },
        };
        const { minW, minH, maxW, maxH } = dimensions;
        const { h: oldHeight, w: oldWidth } = oldDashlet.layout;
        if (minW && oldWidth < minW) {
          updatePayload.payload.layout.w = minW;
        }
        if (maxW && oldWidth > maxW) {
          updatePayload.payload.layout.w = maxW;
        }
        if (minH && oldHeight < minH) {
          updatePayload.payload.layout.h = minH;
        }
        if (maxH && oldHeight > maxH) {
          updatePayload.payload.layout.h = maxH;
        }
        updatePayload.layoutUpdate = layoutUpdate;
        await updateDashletMutation.mutateAsync(
          toAiBlockSafeUpdate(updatePayload, oldDashlet, aiSessionDashletId)
        );
      } else {
        if (isDuplicate) {
          await duplicateDashlet({
            payload,
            oldDashlet,
            dashlets,
            dashboardId,
          });
        } else {
          await createDashletMutation.mutateAsync({
            dashboardId,
            payload,
          });
        }
      }
    },
    [
      createDashletMutation,
      updateDashletMutation,
      getDashletPosition,
      dashlets,
      duplicateDashlet,
      aiSessionDashletId,
    ]
  );

  const [addDashletModalProps, addDashletModalTriggerProps] = useModal({
    handleSubmit: (dashlet, builderData) => {
      if (!dashlet) {
        return mutateStaticContentBlock({
          dashboardId: currentDashboard.id,
          builderData,
        });
      }
      if (dashlet?.type === AI_BLOCK_TYPE) {
        // The AI sidebar creates the dashlet on the agent's first push
        openAiBlockCreate();
        return;
      }
      if (dashlet?.type === 'plugin_block') {
        return mutatePluginBlock({
          dashboardId: currentDashboard.id,
          result: dashlet,
        });
      }
      return mutateDashletForDashboardOnly({
        dashboardId: currentDashboard.id,
        dashletData: dashlet,
        isUpdate: false,
      });
    },
  });

  const handleClickEditDashboards = useCallback(() => {
    history.push('/dashboard/settings');
  }, [history]);

  const createDashboardMutation = useMutation({
    mutationFn: (result) => {
      return createDashboardQuery(result, undefined, 'generic_dashboard');
    },
    onSuccess: (data) => {
      afterCreate(data.id);
      refetchDashboards();
      deferExecution(() => {
        history.push(`/dashboard/${data.id}`);
      });
    },
  });

  const handleCreateDashboard = useCallback(
    ({ name, permissions, isPrivate, styleSettings }) => {
      createDashboardMutation.mutate({
        name,
        style_settings: styleSettings,
        sharing_settings: {
          ...buildSharingSettingsForAPI(permissions).sharing_settings,
          private: isPrivate,
        },
      });
    },
    [createDashboardMutation]
  );

  const [modalProps, , { show: handleShowModal }] = useModal({
    handleSubmit: handleCreateDashboard,
  });

  const handleClickAddDashboard = useCallback(() => {
    handleShowModal();
  }, [handleShowModal]);

  const renderChild = useCallback(
    ({ dashlet, index }) => {
      return renderAiFocusSlot(dashlet, draggable && hasEditPermission, () => (
        <ResettableDashlet
          deleteDashlet={deleteDashlet}
          currentDashboard={currentDashboard}
          dashlet={getDashletWithMergedStyles(dashlet, currentDashboard)}
          onDeleteDashlet={deleteDashlet}
          isDashletUpdating={
            updateDashletMutation.isLoading &&
            updateDashletMutation.variables.dashletId === dashlet.id
          }
          isDraggable={draggable && hasEditPermission}
          duplicateDashlet={(newDashlet) => {
            if (isCustomCodeDashlet(dashlet)) {
              return mutateCustomCodeBlock({
                dashboardId: currentDashboard.id,
                dashletId: dashlet.id,
                isDuplicate: true,
                name: newDashlet.name,
              });
            }
            if (isStaticContentDashlet(dashlet)) {
              return mutateStaticContentBlock({
                dashboardId: currentDashboard.id,
                builderData: dashlet.config.content,
                dashletId: dashlet.id,
                isDuplicate: true,
              });
            }
            if (isPluginContentDashlet(dashlet)) {
              return mutatePluginBlock({
                dashboardId: currentDashboard.id,
                dashletId: dashlet.id,
                isDuplicate: true,
                name: newDashlet.name,
              });
            }
            return mutateDashletForDashboardOnly({
              dashboardId: currentDashboard.id,
              dashletData: {
                ...newDashlet,
                id: dashlet.id,
              },
              isDuplicate: true,
            });
          }}
          onShowEditModal={() => {
            setOverrideShowDashletMenu(false);
            setAutoOpenAIOverlay(false);
            // An AI block with a stored session opens the AI sidebar in focus
            // mode; without one (or an unparseable one) it falls through to
            // the JSON editor below
            if (openAiBlockEdit(dashlet)) {
              return;
            }
            if (isCustomCodeDashlet(dashlet)) {
              openCustomCodeEditor(dashlet);
              return;
            }
            if (isStaticContentDashlet(dashlet)) {
              openStaticContentBuilder(dashlet);
              return;
            }
            if (isPluginContentDashlet(dashlet)) {
              setPluginEditDashlet(dashlet);
              return;
            }
            setCurrentDashlet(dashlet);
            deferExecution(() => handleShowEditDashlet());
          }}
          editDashlet={(
            newDashlet,
            builderData,
            isScheduledActivitiesColumnUpdate
          ) => {
            if (builderData && typeof builderData === 'object' && !newDashlet) {
              return mutateStaticContentBlock({
                dashboardId: currentDashboard.id,
                builderData,
                dashletId: dashlet.id,
              });
            }
            return mutateDashletForDashboardOnly({
              dashboardId: currentDashboard.id,
              dashletData: { ...newDashlet, id: dashlet.id },
              isUpdate: true,
              layoutUpdate: isScheduledActivitiesColumnUpdate,
            });
          }}
          canEdit={hasEditPermission}
          dashboardId={currentDashboard.id}
          setOverrideShowDashletMenu={() => {
            setAutoOpenAIOverlay(false);
            setOverrideShowDashletMenu(true);
            setCurrentDashlet(dashlet);
            deferExecution(() => handleShowEditDashlet());
          }}
          onEditWithAIHandler={
            dashletAiFeaturesEnabled && !isStaticContentDashlet(dashlet)
              ? () => {
                  setCurrentDashlet(dashlet);
                  deferExecution(() => {
                    handleShowEditDashlet();
                    setAutoOpenAIOverlay(true);
                  });
                }
              : undefined
          }
          breakpoint={breakpoint}
          dateFilter={dateFilter}
          teamFilter={teamFilter}
          mobile={mobile}
          clientObjectId={clientObjectId}
          ModalComponent={DashletModal}
          index={index}
          aiSessionDashletId={aiSessionDashletId}
        />
      ));
    },
    [
      renderAiFocusSlot,
      aiSessionDashletId,
      openAiBlockEdit,
      dashletAiFeaturesEnabled,
      breakpoint,
      clientObjectId,
      currentDashboard,
      dateFilter,
      deleteDashlet,
      hasEditPermission,
      updateDashletMutation.isLoading,
      updateDashletMutation.variables,
      mutateDashletForDashboardOnly,
      mutateStaticContentBlock,
      mutatePluginBlock,
      mutateCustomCodeBlock,
      openStaticContentBuilder,
      openCustomCodeEditor,
      handleShowEditDashlet,
      mobile,
      teamFilter,
      draggable,
    ]
  );

  const children = useDashboardGridChildren({
    renderChild,
    forceShowUIElements,
    dashlets,
    defaultStyleSettings: currentDashboard?.styleSettings,
    hasEditPermission,
    breakpoint,
    addDashletModalTriggerProps,
    mobile,
    dateFilter,
    onEditDashlet: (dashlet) => {
      setCurrentDashlet(dashlet);
    },
  });

  const currentDashletWithDefaultStyles = useMemo(
    () => getDashletWithMergedStyles(currentDashlet, currentDashboard),
    [currentDashlet, currentDashboard]
  );

  const requestHandler = useCallback(() => {
    setExistingDashboard({ id: unsafeDashboardId });
    handleShowRequestModal();
  }, [handleShowRequestModal, unsafeDashboardId]);

  const logActivityActions = useMemo(() => {
    return {
      update: () => {},
      save: () => {},
    };
  }, []);

  const aiGenerationLoadingMessages = useMemo(
    () => [
      { text: t('Gathering requirements'), delay: 5000 },
      { text: t('Gathering business context'), delay: 5000 },
      { text: t('Designing dashboard structure'), delay: 5000 },
      { text: t('Building dashlet configuration'), delay: 5000 },
      { text: t('Validating dashboard structure') },
    ],
    [t]
  );

  if (
    currentDashboard === null &&
    !isLoading &&
    hasDashboards &&
    !missingPermission
  ) {
    const fallbackDashboardId = sortedDashboards[0]?.id;
    if (fallbackDashboardId && fallbackDashboardId !== unsafeDashboardId) {
      showToast({
        message: t(
          'The dashboard you tried to view is not accessible to you. Contact your administrator if you think this is in error'
        ),
        variant: toastVariant.FAILURE,
      });
      setCurrentDashboardId(fallbackDashboardId);
      return (
        <Redirect to={`/dashboard/${fallbackDashboardId}${location.search}`} />
      );
    }
  }

  if (
    currentDashboard?.type === 'homepage' ||
    currentDashboard?.type === 'chart_group'
  ) {
    const fallbackDashboardId = sortedDashboards?.filter((d) => !d.hidden)?.[0]
      ?.id;
    setCurrentDashboardId(fallbackDashboardId);
    return (
      <Redirect
        to={`${getDashboardUrl(
          currentDashboard,
          currentDashboard?.customObject,
          clientObjectId
        )}${location.search}`}
      />
    );
  }

  if (isLoading) {
    return <Loader loading={isLoading} />;
  }

  return (
    <DashboardStateWrapper
      setPersistentDashboardState={setPersistentDashboardState}
      persistentDashboardState={persistentDashboardState}
    >
      <OverflowPortalContext.Provider value={overflowPortalContainer}>
        <DashboardGridPageContainer isMobile={mobile}>
          {dashboardsAccess.view ? (
            <SubNav
              currentDashboard={currentDashboard}
              allDashboards={sortedDashboards}
              setDashboard={(id) => history.push(`/dashboard/${id}`)}
              triggerAddDashletProps={addDashletModalTriggerProps}
              setDateFilter={setDateFilter}
              teamFilter={teamFilter}
              dateFilter={dateFilter}
              handleClickEditDashboards={handleClickEditDashboards}
              handleClickAddDashboard={handleClickAddDashboard}
              hasEditPermission={hasEditPermission}
              roleOptions={roles ?? []}
              onChangeGroupedValue={setTeamFilter}
              missing={!currentDashboard}
              unavailable={doesNotExist || missingPermission}
              ranges={ranges}
              todayDateInBusinessTimeZone={todayDateInBusinessTimeZone}
              dashboardsAccess={dashboardsAccess}
              aiFeaturesEnabled={aiFeaturesEnabled}
              onGenerationSuccess={onGenerationSuccess}
              onGenerationFailure={onGenerationFailure}
              extractGenerationResult={extractGenerationResult}
              aiGenerationLoadingMessages={aiGenerationLoadingMessages}
            />
          ) : null}
          <PiChatSideLayout panel={aiBlockPanel}>
            <DashboardGridContainerBox>
              <BreakpointContainer className="breakpoint-container">
                {isFlagEnabled('grid-breakpoints') ? (
                  <div className="bp-container">
                    <div className="bp bp-xxs">XXS</div>
                    <div className="bp bp-xs">XS</div>
                    <div className="bp bp-sm">SM</div>
                    <div className="bp bp-md">MD</div>
                    <div className="bp bp-lg">LG</div>
                    <div className="bp bp-xl">XL</div>
                    <div className="bp bp-xxl">XXL</div>
                  </div>
                ) : null}
                {!currentDashboard && (
                  <>
                    {!dashboardsAccess.view ? (
                      <ErrorMessage
                        title={t('Permissions Needed')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'You do not have access to view dashboards. If you believe you should have these permissions, please contact your Business Administrator.'
                            )}
                          </DashboardWarningText>
                        }
                        code={
                          <DashboardErrorCodeText as="p">
                            {t('Error code')}: 403
                          </DashboardErrorCodeText>
                        }
                        dashboard
                      />
                    ) : missingPermission ? (
                      <ErrorMessage
                        title={t('Permissions Needed')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'The dashboard you are trying to view requires additional permissions. If you believe you should have permissions to view, please request an access-level increase from a Dashboard Administrator.'
                            )}
                          </DashboardWarningText>
                        }
                        button={
                          <Button color="blue" onClick={requestHandler}>
                            {t('Request Access')}
                          </Button>
                        }
                        code={
                          <DashboardErrorCodeText as="p">
                            {t('Error code')}: 403
                          </DashboardErrorCodeText>
                        }
                        dashboard
                      />
                    ) : doesNotExist ? (
                      <ErrorMessage
                        title={t('Not Found')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'The dashboard you are trying to view does not exist. If you believe this is in error, please contact your Business Administrator.'
                            )}
                          </DashboardWarningText>
                        }
                        code={
                          <DashboardErrorCodeText as="p">
                            {t('Error code')}: 404
                          </DashboardErrorCodeText>
                        }
                        dashboard
                      />
                    ) : !hasDashboards ? (
                      <ErrorMessage
                        title={t('No Dashboards Available')}
                        text={
                          <DashboardWarningText as="p">
                            {t(
                              'There are currently no dashboards that you have access to in this business. To get started, please click the Dashboard Settings button at the top right of the screen or the Add Dashboard button below.'
                            )}
                          </DashboardWarningText>
                        }
                        button={
                          <Button
                            color="green"
                            onClick={handleClickAddDashboard}
                          >
                            {t('Add Dashboard')}
                          </Button>
                        }
                        dashboard
                      />
                    ) : null}
                  </>
                )}
                {currentDashboard && currentDashboard.dashlets && (
                  <DashboardGrid
                    updateLayouts={updateLayouts}
                    resizeHandles={resizeHandles}
                    rowHeight={rowHeight}
                    editable={editable}
                    setBreakpoint={setBreakpoint}
                    setContainerWidth={setContainerWidth}
                  >
                    {children}
                  </DashboardGrid>
                )}
              </BreakpointContainer>
            </DashboardGridContainerBox>
          </PiChatSideLayout>
        </DashboardGridPageContainer>
        <BlockModal
          {...addDashletModalProps}
          mode={MODES.DASHBOARD}
          currentDashboard={currentDashboard}
        />
        {staticContentBuilderOpen && (
          <HomepageBuilder {...staticContentBuilderProps} />
        )}
        {customCodeEditorProps && (
          <CustomCodeBlockEditor {...customCodeEditorProps} />
        )}
        {pluginEditDashlet && (
          <PluginBlockWizard
            dashlet={pluginEditDashlet}
            mode={MODES.DASHBOARD}
            onHide={() => setPluginEditDashlet(null)}
            onConfirm={(result) => {
              const id = pluginEditDashlet.id;
              setPluginEditDashlet(null);
              mutatePluginBlock({
                dashboardId: currentDashboard.id,
                dashletId: id,
                result,
              });
            }}
          />
        )}
        {currentDashlet?.id && !hasEditPermission ? null : (
          <DashletModal
            {...editDashletModalProps}
            existing={
              currentDashletWithDefaultStyles.config
                ? editableDashlet(currentDashletWithDefaultStyles, t)
                : {}
            }
            currentDashboard={currentDashboard}
            scrollActvitiyBuilderIntoView={overrideShowDashletMenu}
            autoOpenAIOverlay={autoOpenAIOverlay}
            hideModal={() => {
              setOverrideShowDashletMenu(false);
              setAutoOpenAIOverlay(false);
              handleHideEditDashlet();
              setCurrentDashlet({});
            }}
            clientObjectId={clientObjectId}
          />
        )}
        {activityIdValue && activityIdValue !== '' ? (
          <BasicModalWithConfirmation
            show
            buttonText={t('CLOSE')}
            heading={t('Scheduled Activities')}
            leftBtn={null}
            defaultLeftBtnText={t('Cancel')}
            size={'large'}
            onConfirm={() => setActivityIdValue('')}
            onHide={() => setActivityIdValue('')}
            displayFlex
            className="BasicModal"
            actionButton
            actionBtnColor="blue"
            enforceFocus={false}
            dirty={activityNoteDirty}
          >
            <ModalContentWrapper type="ACTIVITIES">
              <ScheduledActivities
                blockMetadata={{
                  columnSettings: [],
                }}
                whatFor={{}}
                onUpdateSources={() => {}}
                logActivityActions={logActivityActions}
                handleNoteUpdated={setActivityNoteDirty}
                fieldStateDirty={false}
                updateData={() => {}}
                isCustomObject={false}
                scheduledActivityId={activityIdValue}
                currentObject={{}}
                disableFilterToggle
                showModal={activityIdValue && activityIdValue !== ''}
                inModal
                displaySingleActivityPopup
                handleCloseModal={() => setActivityIdValue('')}
              />
            </ModalContentWrapper>
          </BasicModalWithConfirmation>
        ) : null}
        <DashboardStyleSettingsProvider
          styleSettings={DEFAULT_DASHLET_STYLE_SETTINGS}
        >
          <CreateLayoutModal
            {...modalProps}
            canUserEditPermissions
            showEditAdminOptions
            entity={t('Dashboard')}
            forceAdmin
          />
        </DashboardStyleSettingsProvider>
        <RequestModal
          {...requestModalProps}
          dashboard={existingDashboard}
          employeeSectionAccess={dashboardsAccess}
          buttonMode
        />
      </OverflowPortalContext.Provider>
      <PortalContainer ref={setOverflowPortalContainer} zIndex={1} />
    </DashboardStateWrapper>
  );
};

const DashboardPageWithDataManager = ({ title }) => {
  const { dashboardId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activityId = queryParams.get(scheduledActivityPopupQueryParam);

  if (!dashboardId) {
    return <DashboardPage title={title} activityId={activityId} />;
  }

  return (
    <DataManagerWrapper key={dashboardId} instanceId={dashboardId}>
      <DashboardPage title={title} activityId={activityId} />
    </DataManagerWrapper>
  );
};

export default DashboardPageWithDataManager;
